import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { watchlistActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import {
    PermissionType,
    WatchlistCategoryType,
    WatchlistCategoryTypeOptions,
    WatchlistCureType,
    WatchlistCureTypeOptions,
    LoanGradeTypeOptions,
    LoanStatusType,
    LoanType,
} from '@apply/constants';
import {
    findArrayIndexByPropertyName,
    formatDateYearMonth,
    handleAdminError,
    handleAdminSuccess,
} from '../../common/SharedFunctions';
import { getLoanTypeName, getLoanTermOptions } from '../../loans/common/LoanFunctions';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faEye, faPencil, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { RefreshEvents, ConfirmModal, formatDateMonthDayYear, hasPermission } from '@apply/ui';
import {
    EntityConcernType,
    EntityConcernTypeOptions,
    EntityResponsivenessTypeOptions,
} from '../../borrowers/common/BorrowerConstants';
import { WatchlistIssueModal } from './WatchlistIssueModal';
import { NumericFormat } from 'react-number-format';
import { NotesGrid } from '../common/WatchlistComponents';
import moment from 'moment-timezone';
import DateBox from 'devextreme-react/date-box';
import { GradeReasonModal } from '../../loans/modals/GradeReasonModal';
import { isLoanLoc } from '../../loans/common/functions/isLoanLoc';

import { monthDiff, addMonths } from '../../common/SharedFunctions';
import { WatchlistDetailNoteType } from '../common/WatchlistConstants';

export const saveStages = {
    validateMaturityDate: 1,
    save: 2,
};

const WatchlistLoanDetailsModal = () => {
    const { isOpen, watchlistId, loanId, propertyId } = useSelector((state) => state.watchlists.watchlistLoanDetailModal);

    const { dispatch } = store;
    const [openConfirmCancelModal, set_openConfirmCancelModal] = useState(false);

    const [wlItem, set_wlItem] = useState(null);

    const [isSubNote, set_isSubNote] = useState(false);
    const [grade, set_grade] = useState('');
    const [gradeReasonVisible, set_gradeReasonVisible] = useState(false);
    const [gradeReason, set_gradeReason] = useState('');

    const [inForbearance, set_inForbearance] = useState(false);
    const [maturityDate, set_maturityDate] = useState(null);
    const [forbearanceAgreementTerms, set_forbearanceAgreementTerms] = useState('');

    const [itemNotes, set_itemNotes] = useState([]);
    const [issues, set_issues] = useState([]);
    const [liquidity, set_liquidity] = useState(null);
    const [actionPlan, set_actionPlan] = useState(null);
    const [deletingNotes, set_deletingNotes] = useState(0);

    const [entityNotes, set_entityNotes] = useState('');
    const [entityConcernTypeId, set_entityConcernTypeId] = useState('');
    const [entityConcernReason, set_entityConcernReason] = useState('');
    const [entityResponsivenessTypeId, set_entityResponsivenessTypeId] = useState('');
    const [entityWorkingRelationship, set_entityWorkingRelationship] = useState('');

    const [loadingMessage, set_loadingMessage] = useState('');
    const [issueToEdit, set_issueToEdit] = useState(null);

    //Maturity Date Warnings
    const [maturityDateWarning, set_maturityDateWarning] = useState(false);
    const [maturityDateWarningText, set_maturityDateWarningText] = useState('');
    const [confirmMaturityDateModalText, set_confirmMaturityDateModalText] = useState('');
    const [confirmMaturityDateModalOpen, set_confirmMaturityDateModalOpen] = useState(false);

    //FDILA Terms Warning
    const [confirmFDILAAgreementTextRemovalWarningOpen, set_confirmFDILAAgreementTextRemovalWarningOpen] = useState(false);
    const [confirmFDILAAgreementTextRemovalWarningText, set_confirmFDILAAgreementTextRemovalWarningText] = useState(
        'Removing this Agreement will also remove the Agreement Terms.'
    );

    const [issueModalTitle, setIssuesModalTitle] = useState('Add Issue');
    const refreshEvents = new RefreshEvents();
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { padding: '10px 5px' },
        formRow: { marginBottom: '0' },
        infoLabel: { paddingBottom: '0' },
        infoData: { paddingBottom: '0', fontWeight: 'bold' },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            padding: '5px',
            margin: '0px 10px 5px 10px',
            fontSize: 'smaller',
        },
        infoSectionProperty: {
            border: '1px solid grey',
            backgroundColor: 'whitesmoke',
            borderRadius: '5px',
            padding: '4px',
            margin: '10px 5px 5px 5px',
            fontSize: 'small',
        },
        infoSectionSubheader: {
            fontWeight: 'bold',
            textDecoration: 'underline',
            fontSize: 'small',
        },
        sectionheader: {
            margin: '5px 10px',
        },
        inforbearance: { fontSize: '0.75rem', paddingTop: '35px' },
        warning: {
            padding: '10px',
            color: 'darkred',
            width: '100%',
            textAlign: 'center',
            fontSize: 'smaller',
        },
    };

    const unsavedChangesExist = () => {
        let unsavedChanges = false;

        unsavedChanges =
            wlItem.itemNotes !== itemNotes ||
            wlItem.issues !== issues ||
            wlItem.liquidity !== liquidity ||
            wlItem.actionPlan !== actionPlan ||
            wlItem.entityNotes !== entityNotes ||
            (wlItem.entityConcernTypeId ?? '') !== entityConcernTypeId ||
            (wlItem.entityConcernReason ?? '') !== entityConcernReason ||
            (wlItem.entityResponsivenessTypeId ?? '') !== entityResponsivenessTypeId ||
            wlItem.entityWorkingRelationship !== entityWorkingRelationship ||
            wlItem.maturityDate !== maturityDate ||
            wlItem.inForbearance !== inForbearance ||
            wlItem.forbearanceAgreementTerms !== forbearanceAgreementTerms;

        return unsavedChanges;
    };

    const _handleClose = () => {
        if (unsavedChangesExist()) {
            set_openConfirmCancelModal(true);
            return;
        }
        _closeWatchlistIssueModal();
    };

    const _handleCancelConfirm = async () => {
        set_openConfirmCancelModal(false);
        _closeWatchlistIssueModal();
    };

    const _handleCancelCancel = () => {
        set_openConfirmCancelModal(false);
    };

    const _closeWatchlistIssueModal = () => {
        dispatch(
            watchlistActions.setWatchlistLoanDetailModal({
                watchlistLoanDetailModal: { isOpen: false },
            })
        );
    };

    const confirmMaturityDate_confirm = async () => {
        set_confirmMaturityDateModalOpen(false);
        await validateSubmission(null, saveStages.save);
    };

    const confirmMaturityDate_cancel = () => {
        set_confirmMaturityDateModalOpen(false);
        set_confirmMaturityDateModalText('');
    };

    const confirmFDILAAgreementTextRemoval_confirm = async () => {
        set_confirmFDILAAgreementTextRemovalWarningOpen(false);
        set_inForbearance(false);
        set_forbearanceAgreementTerms('');
    };

    const confirmFDILAAgreementTextRemoval_cancel = () => {
        set_confirmFDILAAgreementTextRemovalWarningOpen(false);
    };

    const resetState = () => {
        set_wlItem(null);
        set_isSubNote(false);
        set_inForbearance(false);
        set_maturityDate(null);
        set_forbearanceAgreementTerms('');
        set_itemNotes([]);
        set_issues([]);
        set_liquidity(null);
        set_actionPlan(null);
        set_entityNotes('');
        set_entityConcernTypeId('');
        set_entityConcernReason('');
        set_entityResponsivenessTypeId('');
        set_entityWorkingRelationship('');
        set_grade('');
        set_gradeReason('');
    };

    const setState = (wl) => {
        set_wlItem(wl);

        const isSN = isLoanLoc(wl.loanTypeId);
        set_isSubNote(isSN);

        set_grade(wl.grade ?? '');
        set_inForbearance(wl.inForbearance ?? false);
        if (wl.maturityDate) {
            set_maturityDate(wl.maturityDate);
        }
        set_forbearanceAgreementTerms(wl.forbearanceAgreementTerms);
        set_itemNotes(wl.itemNotes ?? []);
        set_issues(wl.issues ?? []);
        set_liquidity(wl.liquidity);
        set_actionPlan(wl.actionPlan);
        set_entityNotes(wl.entityNotes ?? []);
        set_entityConcernTypeId(wl.entityConcernTypeId ?? '');
        set_entityConcernReason('');
        set_entityResponsivenessTypeId(wl.entityResponsivenessTypeId ?? '');
        set_entityWorkingRelationship(wl.entityWorkingRelationship ?? '');
    };

    const loadLoanInfoAsync = async (loanId, watchlistId) => {
        try {
            set_loadingMessage('Loading...');

            const response = await watchlistApi.getWatchlistLoanDetails(watchlistId, loanId);
            if (response.statusCode !== 200) {
                return;
            }

            const ln = response.result;
            setState(ln);
            if (!ln.issues || ln.issues.length === 0) {
                addNewIssue();
            }
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please refresh the page');
        } finally {
            set_loadingMessage('');
        }
    };

    const loadLoanPropertyInfoAsync = async (propertyId, watchlistId) => {
        try {
            set_loadingMessage('Loading...');

            const response = await watchlistApi.getWatchlistPropertyDetails(watchlistId, propertyId);
            if (response.statusCode !== 200) {
                return;
            }

            const ln = response.result;
            setState(ln);
            if (!ln.issues || ln.issues.length === 0) {
                addNewIssue();
            }
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please refresh the page');
        } finally {
            set_loadingMessage('');
        }
    };

    const onCategoryTypeRender = (e) => {
        const catgId = e.data.categoryTypeId;
        if (catgId == WatchlistCategoryType.Other) {
            return e.data.otherCategoryName ?? 'Category Name Not Set';
        }
        var option = WatchlistCategoryTypeOptions.find((a) => a.id === catgId);
        if (!option) {
            return 'Unknown';
        }
        return option.name;
    };

    const onCureTypeRender = (e) => {
        const cureId = e.data.cureTypeId;
        if (cureId == WatchlistCureType.Other) {
            return e.data.otherCureName ?? 'Cure Name Not Set';
        }
        var option = WatchlistCureTypeOptions.find((a) => a.id === cureId);
        if (!option) {
            return null;
        }
        return option.name;
    };

    const onIsOutstandingRender = (e) => {
        var cure = WatchlistCureTypeOptions.find((a) => a.id === e.data.cureTypeId);
        if (!cure) {
            return null;
        }

        if (e.data.isOutstanding) {
            return (
                <span>
                    <i className='green-text'>
                        <FontAwesomeIcon icon={faCheck} />
                    </i>
                </span>
            );
        } else {
            return (
                <span>
                    <i className='red-text'>
                        <FontAwesomeIcon icon={faTimes} />
                    </i>
                </span>
            );
        }
    };

    const onRenderLoanTerm = () => {
        const termsByLoan = getLoanTermOptions(wlItem.loanTypeId);
        if (termsByLoan && wlItem.initialTermMonths) {
            // Find matching term by comparing all three values
            const matchingTerms = termsByLoan.filter(
                (t) =>
                    t.initialTermMonths === wlItem.initialTermMonths &&
                    t.contractualExtension1Months === wlItem.contractualExtension1Months &&
                    t.contractualExtension2Months === wlItem.contractualExtension2Months
            );
            if (matchingTerms && matchingTerms.length === 1) {
                return matchingTerms[0].name;
            }
            // If no exact match, display the values directly
            let termDisplay = `${wlItem.initialTermMonths} Months`;
            if (wlItem.contractualExtension1Months) {
                termDisplay += ` + ${wlItem.contractualExtension1Months}`;
            }
            if (wlItem.contractualExtension2Months) {
                termDisplay += ` + ${wlItem.contractualExtension2Months}`;
            }
            return termDisplay;
        }
        return 'Not Set';
    };

    const onIssueModalSubmit = (issue) => {
        const issuesArray = [...issues];
        if (!issue.guid) {
            issue.guid = issuesArray.filter((x) => x.isDirty).length + 1;
            issue.isDirty = true;
            issuesArray.push(issue);
        } else {
            const index = findArrayIndexByPropertyName(issuesArray, 'guid', issue.guid);

            if (~index) {
                issuesArray[index] = issue;
            } else {
                issuesArray.push(issue);
            }
        }

        set_issues(issuesArray);
        set_issueToEdit(null);
    };

    const onIssueModalCancel = () => {
        set_issueToEdit(null);
    };

    const addNewIssue = () => {
        setIssuesModalTitle('Add Issue');
        //Any category not already in the issues array is permissable
        const allowedCategories = [];
        WatchlistCategoryTypeOptions.forEach((i) => {
            const item = issues.find((a) => a.categoryTypeId === i.id);
            if (!item) {
                allowedCategories.push({ ...i });
            }
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const model = {
            allowedCategories,
            issueDate: today,
            categoryTypeId: null,
            otherCategoryName: '',
            curedDate: null,
            cureTypeId: null,
            otherCuredName: '',
            isOutstanding: false,
            liquidity: null,
            maturityDate: null,
            inForbearance: false,
            forbearanceAgreementTerms: '',
            grade: null,
        };
        set_issueToEdit(model);
    };

    const onIssuesCellDblClick = (e) => {
        let issue = issues.find((a) => a.categoryTypeId === e.data.categoryTypeId);
        if (!issue) {
            return;
        }

        //Any category not already in the issues array is permissable
        issue = { ...issue };
        const allowedCategories = [];
        WatchlistCategoryTypeOptions.forEach((i) => {
            if (issue.categoryTypeId == i.id) {
                allowedCategories.push({ ...i });
            } else {
                const item = issues.find((a) => a.categoryTypeId === i.id);
                if (!item) {
                    allowedCategories.push({ ...i });
                }
            }
        });

        issue.allowedCategories = allowedCategories;
        set_issueToEdit(issue);
    };

    const onDeleteIssue = (e) => {
        const array = [...issues];
        const issue = issues.find((a) => a.categoryTypeId === e.data.categoryTypeId);
        if (!issue) {
            return;
        }
        issue.isDeleted = !issue.isDeleted;
        set_issues(array);
    };

    const onIssueActionsRender = (e) => {
        const title = !canEdit() ? 'View Issue' : 'Edit Issue';
        return (
            <div>
                <button
                    type='button'
                    id='editIssue'
                    className='gridButton'
                    title={title}
                    onClick={(event) => {
                        setIssuesModalTitle(title);
                        onIssuesCellDblClick(e);
                    }}
                >
                    <FontAwesomeIcon icon={!canEdit() ? faEye : faPencil} />
                </button>
                &nbsp;
                <button
                    type='button'
                    id='deleteIssue'
                    className='gridButton'
                    title={e.data.isDeleted ? 'Will be deleted on save - Click to undelete' : 'Delete'}
                    disabled={!canEdit()}
                    onClick={(event) => onDeleteIssue(e)}
                >
                    <FontAwesomeIcon
                        icon={faTrash}
                        style={e.data.isDeleted ? { color: 'red' } : { color: '#2c5591' }}
                    />
                </button>
            </div>
        );
    };

    const getLoanModelToSave = () => {
        return {
            watchlistId,
            loanId: wlItem.loanId,
            itemNotes,
            issues: issues.filter((x) => {
                // @ts-ignore
                if (!x.isDirty) {
                    return x;
                }
                // @ts-ignore
                if (x.isDirty && !x.isDeleted) {
                    // @ts-ignore
                    x.guid = undefined;
                    // @ts-ignore
                    x.isDirty = undefined;
                    return x;
                }
            }),
            entityConcernTypeId,
            entityResponsivenessTypeId,
            entityWorkingRelationship,
            entityNotes,
            entityConcernReason,
            liquidity,
            actionPlan,
            maturityDate,
            inForbearance,
            forbearanceAgreementTerms,
            grade,
            gradeReason,
        };
    };

    const getPropertyModelToSave = () => {
        return {
            watchlistId,
            propertyId: wlItem.propertyId,
            itemNotes,
            issues: issues.filter((x) => {
                // @ts-ignore
                if (!x.isDirty) {
                    return x;
                }
                // @ts-ignore
                if (x.isDirty && !x.isDeleted) {
                    // @ts-ignore
                    x.guid = undefined;
                    // @ts-ignore
                    x.isDirty = undefined;
                    return x;
                }
            }),
            entityConcernTypeId,
            entityResponsivenessTypeId,
            entityWorkingRelationship,
            entityNotes,
            entityConcernReason,
            liquidity,
            actionPlan,
            maturityDate,
            inForbearance,
            forbearanceAgreementTerms,
            grade,
            gradeReason,
        };
    };

    const validateSubmission = async (e, step) => {
        if (e) {
            e.preventDefault();
        }
        step = step ?? saveStages.validateMaturityDate;

        //Alert if the Maturity Date is potentially wrong
        if (step === saveStages.validateMaturityDate) {
            if (maturityDateWarning) {
                //alert and continue if confirmed
                set_confirmMaturityDateModalText(maturityDateWarningText);
                set_confirmMaturityDateModalOpen(true);
                return false;
            }
            step = saveStages.save;
        }

        if (step === saveStages.save) {
            await saveChanges();
        }
    };

    const saveChanges = async () => {
        try {
            if (propertyId) {
                const propertyModel = getPropertyModelToSave();
                set_loadingMessage('Saving...');
                const response = await watchlistApi.updateProperty(propertyModel);
                if (response.statusCode != 200) {
                    throw new Error('Failed to save changes to Watchlist Property!');
                }
            } else if (loanId) {
                const loanModel = getLoanModelToSave();
                set_loadingMessage('Saving...');
                const response = await watchlistApi.updateLoan(loanModel);
                if (response.statusCode != 200) {
                    throw new Error('Failed to save changes to Watchlist Loan!');
                }
            } else {
                throw new Error('Watchlist item not saved - no item information provided.');
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Watchlist saved successfully.');

            setTimeout(() => {
                _closeWatchlistIssueModal();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, ex.message); // "An unexpected error occurred! Please try again.");
        } finally {
            set_loadingMessage('');
        }
    };

    const canEdit = () => {
        return !wlItem.isLocked && hasPermission(PermissionType.WatchlistEdit);
    };

    const canSubmit = () => {
        return canEdit() && issues.filter((i) => !i.isDeleted).length > 0 && validateFields();
    };

    const validateFields = () => {
        var fieldsValid = true;
        if (wlItem.noteDate && !maturityDate) {
            fieldsValid = false;
        }
        return fieldsValid;
    };

    const getConcernStyle = (typeId) => {
        if (!typeId) {
            return null;
        }

        switch (parseInt(typeId)) {
            case EntityConcernType.Green:
                return { backgroundColor: '#86c786' };
            case EntityConcernType.Yellow:
                return { backgroundColor: '#e2e273' };
            case EntityConcernType.Red:
                return { backgroundColor: '#f86363' };
            default:
                return null;
        }
    };

    const onGradeChange = (newGrade) => {
        if (wlItem.statusId !== LoanStatusType.Approved && newGrade !== grade) {
            set_gradeReasonVisible(true);
        }
        set_grade(newGrade);
    };

    const onGradeReasonSubmit = (reason) => {
        set_gradeReason(reason);
        set_gradeReasonVisible(false);
    };

    const onGradeReasonCancel = () => {
        set_grade(wlItem.grade ?? '');
        set_gradeReasonVisible(false);
    };

    const toggleInForbearance = (val) => {
        if (inForbearance && val !== true && forbearanceAgreementTerms?.length > 0) {
            set_confirmFDILAAgreementTextRemovalWarningOpen(true);
            return;
        }
        set_inForbearance(val);
    };

    useEffect(() => {
        if (!loanId && !propertyId) {
            resetState();
            return;
        }

        const loadAsync = async (loanId, propertyId, watchlistId) => {
            if (propertyId) {
                await loadLoanPropertyInfoAsync(propertyId, watchlistId);
            } else if (loanId) {
                await loadLoanInfoAsync(loanId, watchlistId);
            }
        };

        loadAsync(loanId, propertyId, watchlistId);
    }, [loanId, propertyId, watchlistId]);

    useEffect(() => {
        if (!wlItem || !wlItem.noteDate) {
            return;
        }

        if (!isSubNote && wlItem.initialTermMonths) {
            const noteDate = new Date(wlItem.noteDate);
            //If the number of months is approximately correct then don't update the maturity date
            var months = monthDiff(noteDate, new Date(maturityDate));
            if (months !== wlItem.initialTermMonths) {
                const dt = noteDate.getTime();
                if (!wlItem.maturityDate) {
                    set_maturityDate(addMonths(dt, wlItem.initialTermMonths));
                } else {
                    set_maturityDateWarning(true);
                    if (months > wlItem.initialTermMonths) {
                        set_maturityDateWarningText('Maturity Date Is Past the Loan Terms.');
                    } else {
                        set_maturityDateWarningText('Maturity Date Is Less than the Loan Terms.');
                    }
                }
            } else {
                set_maturityDateWarning(false);
                set_maturityDateWarningText('');
            }
        }
    }, [wlItem, maturityDate]);

    if (!isOpen || !watchlistId) {
        return null;
    }

    if (!isOpen || !wlItem) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '800px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div>
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1 style={{ whiteSpace: 'nowrap' }}>Watchlist Detail</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <div
                                        style={styles.form}
                                        className={'standard-form'}
                                    >
                                        <h2
                                            className='decorated'
                                            style={styles.sectionheader}
                                        >
                                            Loan Summary
                                        </h2>
                                        <div
                                            className='row'
                                            style={styles.infoSection}
                                        >
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                Borrower:
                                            </div>
                                            <div
                                                className='columns small-12 large-10'
                                                style={styles.infoData}
                                            >
                                                {wlItem.entityName}
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                BFI #:
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoData}
                                            >
                                                {wlItem.bfiNumber}
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                Type:
                                            </div>
                                            <div
                                                className='columns small-12 large-6'
                                                style={styles.infoData}
                                            >
                                                {getLoanTypeName(wlItem.loanTypeId)}
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                Note Date:
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoData}
                                            >
                                                {moment(wlItem.noteDate).format('MM-DD-YYYY')}
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                POC:
                                            </div>
                                            <div
                                                className='columns small-12 large-6'
                                                style={styles.infoData}
                                            >
                                                {wlItem.pocName ?? 'Not Set'}
                                            </div>
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.infoLabel}
                                            >
                                                Term:
                                            </div>
                                            <div
                                                className='columns small-12 large-6'
                                                style={styles.infoData}
                                            >
                                                {onRenderLoanTerm()}
                                            </div>
                                            {wlItem.propertyId && (
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.infoLabel}
                                                >
                                                    <div
                                                        className='row'
                                                        style={styles.infoSectionProperty}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.infoSectionSubheader}
                                                        >
                                                            Property Address
                                                        </div>
                                                        <div className='columns small-12 large-6'>
                                                            <div style={styles.infoLabel}>Street Address:</div>
                                                            <div style={styles.infoData}>{wlItem.street1 ?? ''}</div>
                                                        </div>
                                                        <div className='columns small-12 large-6'>
                                                            <div style={styles.infoLabel}>Street Address 2:</div>
                                                            <div style={styles.infoData}>{wlItem.street2 ?? ''}</div>
                                                        </div>
                                                        <div className='columns small-12 large-3'>
                                                            <div style={styles.infoLabel}>City:</div>
                                                            <div style={styles.infoData}>{wlItem.city ?? ''}</div>
                                                        </div>
                                                        <div className='columns small-12 large-3'>
                                                            <div style={styles.infoLabel}>State:</div>
                                                            <div style={styles.infoData}>{wlItem.state ?? ''}</div>
                                                        </div>
                                                        <div className='columns small-12 large-3'>
                                                            <div style={styles.infoLabel}>Zip:</div>
                                                            <div style={styles.infoData}>{wlItem.zipCode ?? ''}</div>
                                                        </div>
                                                        <div className='columns small-12 large-3'>
                                                            <div style={styles.infoLabel}>County:</div>
                                                            <div style={styles.infoData}>{wlItem.county ?? ''}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <h2
                                            className='decorated'
                                            style={styles.sectionheader}
                                        >
                                            Loan Info
                                        </h2>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-2'
                                                style={styles.formColumn}
                                            >
                                                <label htmlFor='grade'>Grade</label>
                                                <select
                                                    id={'grade'}
                                                    value={grade ?? ''}
                                                    onChange={(event) => onGradeChange(parseInt(event.target.value))}
                                                    disabled={!canEdit()}
                                                >
                                                    <option value=''></option>
                                                    {LoanGradeTypeOptions.filter((x) => x.isLoc === isSubNote).map((o) => {
                                                        return (
                                                            <option
                                                                key={o.id}
                                                                value={o.id}
                                                            >
                                                                {o.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                <GradeReasonModal
                                                    isOpen={gradeReasonVisible}
                                                    reason={gradeReason}
                                                    onSubmit={onGradeReasonSubmit}
                                                    onCancel={onGradeReasonCancel}
                                                />
                                            </div>
                                            <div
                                                className='columns small-12 large-3'
                                                style={styles.formColumn}
                                            >
                                                <label
                                                    htmlFor='maturityDate'
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    Maturity Date {wlItem.noteDate && <span className='req'>*</span>}
                                                </label>
                                                <DateBox
                                                    id='maturityDate'
                                                    value={maturityDate}
                                                    onValueChange={set_maturityDate}
                                                    required={wlItem.noteDate}
                                                    disabled={!canEdit() || isSubNote}
                                                />
                                                {maturityDateWarning && (
                                                    <div style={styles.warning}>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon
                                                                    style={{ color: 'red' }}
                                                                    icon={faExclamationTriangle}
                                                                />
                                                            </span>
                                                            <span className='text'>{maturityDateWarningText}</span>
                                                        </span>
                                                        &nbsp;Date Warning!
                                                    </div>
                                                )}
                                            </div>
                                            {(wlItem.statusId === LoanStatusType.Outstanding ||
                                                wlItem.statusId === LoanStatusType.Repaid) && (
                                                <>
                                                    <div
                                                        className='columns small-12 large-4'
                                                        style={styles.inforbearance}
                                                    >
                                                        <input
                                                            type='checkbox'
                                                            checked={inForbearance}
                                                            onChange={(e) => toggleInForbearance(e.target.checked)}
                                                            title='Does this loan have a FDILA agreement?'
                                                            disabled={!canEdit()}
                                                        />
                                                        &nbsp; Under a FDILA Agreementasdf?
                                                    </div>
                                                    {inForbearance && (
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='forbearanceAgreementTerms'>
                                                                Terms of FDILA Agreement:
                                                            </label>
                                                            <textarea
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                rows={3}
                                                                id='forbearanceAgreementTerms'
                                                                value={forbearanceAgreementTerms ?? ''}
                                                                onChange={(event) =>
                                                                    set_forbearanceAgreementTerms(event.target.value)
                                                                }
                                                                disabled={!canEdit()}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <h2
                                            className='decorated'
                                            style={styles.sectionheader}
                                        >
                                            Watchlist Info
                                        </h2>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-12'
                                                style={{ paddingBottom: '0' }}
                                            >
                                                <label htmlFor='issues'>Issues:</label>
                                                <DataGrid
                                                    dataSource={issues}
                                                    rowAlternationEnabled={true}
                                                    noDataText='No Issues Saved'
                                                    showBorders={true}
                                                    showRowLines={false}
                                                    remoteOperations={false}
                                                    allowColumnReordering={false}
                                                    allowColumnResizing={true}
                                                    onCellDblClick={onIssuesCellDblClick}
                                                >
                                                    <Column
                                                        dataField='issueDate'
                                                        caption='Issue Date'
                                                        dataType='date'
                                                        width='80'
                                                        alignment={'left'}
                                                    />
                                                    <Column
                                                        dataField='categoryTypeId'
                                                        caption='Category'
                                                        cellRender={onCategoryTypeRender}
                                                        alignment='left'
                                                    />
                                                    <Column
                                                        dataField='curedDate'
                                                        caption='Cured on'
                                                        dataType='date'
                                                        width='80'
                                                    />
                                                    <Column
                                                        dataField='cureTypeId'
                                                        caption='Cure'
                                                        cellRender={onCureTypeRender}
                                                        alignment='left'
                                                    />
                                                    <Column
                                                        dataField='isOutstanding'
                                                        caption='Outst.'
                                                        cellRender={onIsOutstandingRender}
                                                        width='60'
                                                    />
                                                    <Column
                                                        alignment='center'
                                                        cellRender={onIssueActionsRender}
                                                        width={80}
                                                        allowReordering={false}
                                                        allowSorting={false}
                                                        allowResizing={false}
                                                    />
                                                </DataGrid>
                                                <div style={{ marginTop: '5px' }}>
                                                    <button
                                                        type='button'
                                                        className='button primary'
                                                        title='Add a new Issue'
                                                        onClick={addNewIssue}
                                                        disabled={!canEdit()}
                                                    >
                                                        Add Issue
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-12'
                                                style={{ paddingBottom: '0' }}
                                            >
                                                <label
                                                    htmlFor='notes'
                                                    style={{
                                                        paddingRight: 0,
                                                    }}
                                                >
                                                    Notes:{' '}
                                                    {deletingNotes != 0 ? (
                                                        <span
                                                            style={{
                                                                color: 'red',
                                                                float: 'right',
                                                            }}
                                                        >
                                                            Make sure to press{' '}
                                                            <span
                                                                style={{
                                                                    fontStyle: 'italic',
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                Save & Close
                                                            </span>
                                                            , to delete the notes.
                                                        </span>
                                                    ) : undefined}
                                                </label>
                                                <NotesGrid
                                                    notes={itemNotes}
                                                    isLocked={wlItem.isLocked}
                                                    noteType={WatchlistDetailNoteType.Loan}
                                                    onChanged={set_itemNotes}
                                                    setDeleteNote={set_deletingNotes}
                                                />
                                            </div>
                                        </div>

                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <label htmlFor='actionPlan'>Action Plan</label>
                                                <input
                                                    id='actionPlan'
                                                    type='text'
                                                    value={actionPlan}
                                                    maxLength={200}
                                                    onChange={(e) => set_actionPlan(e.target.value)}
                                                    disabled={!canEdit()}
                                                />
                                            </div>
                                        </div>

                                        <h2
                                            className='decorated'
                                            style={styles.sectionheader}
                                        >
                                            Borrower
                                        </h2>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-4'>
                                                <label htmlFor='entityConcernTypeId'>Concern:</label>
                                                <select
                                                    id='entityConcernTypeId'
                                                    value={entityConcernTypeId}
                                                    required={false}
                                                    onChange={(e) => set_entityConcernTypeId(e.target.value)}
                                                    style={getConcernStyle(entityConcernTypeId)}
                                                    disabled={!canEdit()}
                                                >
                                                    <option value=''>Select...</option>
                                                    {EntityConcernTypeOptions.map((r) => {
                                                        return (
                                                            <option
                                                                key={r.id}
                                                                value={r.id}
                                                                style={getConcernStyle(r.id)}
                                                            >
                                                                {r.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            <div className='columns small-12 large-8'>
                                                <label htmlFor='entityResponsivenessTypeId'>Responsiveness:</label>
                                                <select
                                                    id='entityResponsivenessTypeId'
                                                    value={entityResponsivenessTypeId}
                                                    required={false}
                                                    onChange={(e) => set_entityResponsivenessTypeId(e.target.value)}
                                                    disabled={!canEdit()}
                                                >
                                                    <option value=''>Select...</option>
                                                    {EntityResponsivenessTypeOptions.map((r) => {
                                                        return (
                                                            <option
                                                                key={r.id}
                                                                value={r.id}
                                                            >
                                                                {r.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            {wlItem.requiresConcernReason &&
                                                wlItem.entityConcernTypeId != entityConcernTypeId && (
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='entityConcernReason'>
                                                            Reason for Concern Change: <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            type='text'
                                                            value={entityConcernReason}
                                                            required={true}
                                                            onChange={(e) => set_entityConcernReason(e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-12'>
                                                <label htmlFor='entityWorkingRelationship'>Working Relationship:</label>
                                                <input
                                                    type='text'
                                                    id='entityWorkingRelationship'
                                                    value={entityWorkingRelationship}
                                                    maxLength='100'
                                                    onChange={(e) => set_entityWorkingRelationship(e.target.value)}
                                                    disabled={!canEdit()}
                                                />
                                            </div>
                                        </div>

                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-3'>
                                                <label htmlFor='liquidity'>Liquidity:</label>
                                                <NumericFormat
                                                    prefix='$'
                                                    thousandSeparator=','
                                                    id='liquidity'
                                                    placeholder='$'
                                                    value={liquidity}
                                                    decimalScale={2}
                                                    allowNegative={false}
                                                    onValueChange={(values) => {
                                                        set_liquidity(values.floatValue);
                                                    }}
                                                    required={false}
                                                    disabled={!canEdit()}
                                                    isAllowed={(values, sourceInfo) => {
                                                        const value = values?.floatValue;
                                                        return value === undefined || value === null || value > 0;
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div
                                                className='columns small-12 large-12'
                                                style={{ paddingBottom: '0' }}
                                            >
                                                <label htmlFor='notes'>Entity Notes:</label>
                                                <NotesGrid
                                                    notes={entityNotes}
                                                    isLocked={wlItem.isLocked}
                                                    noteType={WatchlistDetailNoteType.Entity}
                                                    onChanged={set_entityNotes}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            type='button'
                                            className='button primary'
                                            data-dismiss='modal'
                                            disabled={!canSubmit()}
                                            onClick={(event) => validateSubmission(event, saveStages.validateMaturityDate)}
                                            title='Save changes and close modal'
                                        >
                                            Save & Close
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            title='Abandon changes and close modal'
                                            onClick={_handleClose}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <WatchlistIssueModal
                issue={issueToEdit}
                onCancel={onIssueModalCancel}
                onSubmit={onIssueModalSubmit}
                title={issueModalTitle}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <ConfirmModal
                isOpen={openConfirmCancelModal}
                text={'Closing will delete changes.'}
                confirmCallback={_handleCancelConfirm}
                cancelCallback={_handleCancelCancel}
                confirmButtonText='Close'
                cancelButtonText='Cancel'
            />
            <ConfirmModal
                isOpen={confirmMaturityDateModalOpen}
                text={confirmMaturityDateModalText}
                confirmCallback={confirmMaturityDate_confirm}
                cancelCallback={confirmMaturityDate_cancel}
                confirmButtonText='Continue'
                cancelButtonText='Cancel'
            />
            <ConfirmModal
                isOpen={confirmFDILAAgreementTextRemovalWarningOpen}
                text={confirmFDILAAgreementTextRemovalWarningText}
                confirmCallback={confirmFDILAAgreementTextRemoval_confirm}
                cancelCallback={confirmFDILAAgreementTextRemoval_cancel}
                confirmButtonText='Continue'
                cancelButtonText='Cancel'
            />
        </>
    );
};

export default WatchlistLoanDetailsModal;
